import { apiClient } from './client';

// Activity Log Types
export interface ActivityLog {
    id: string;
    userId: string;
    eventType: string;
    category: string;
    action: string;
    metadata: Record<string, unknown>;
    ipAddress?: string;
    userAgent?: string;
    requestId?: string;
    status: 'success' | 'failure' | 'warning';
    resourceType?: string;
    resourceId?: string;
    createdAt: string;
    user?: {
        id: string;
        email: string;
        name?: string;
    };
}

export interface ActivityLogPagination {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
}

export interface ActivityLogResponse {
    success: boolean;
    data: {
        logs: ActivityLog[];
        pagination: ActivityLogPagination;
    };
}

export interface ActivityStatsResponse {
    success: boolean;
    data: {
        totalLogs: number;
        byCategory: Record<string, number>;
        byStatus: Record<string, number>;
        recentActivity: Array<{
            id: string;
            eventType: string;
            action: string;
            createdAt: string;
        }>;
    };
}

export interface ActivityFilterOptions {
    limit?: number;
    offset?: number;
    category?: string;
    eventType?: string;
    status?: string;
    resourceType?: string;
    startDate?: string;
    endDate?: string;
}

class ActivityService {
    /**
     * Get user's activity logs with optional filtering
     */
    async getActivity(options: ActivityFilterOptions = {}): Promise<ActivityLogResponse> {
        const params = new URLSearchParams();

        if (options.limit) params.append('limit', String(options.limit));
        if (options.offset) params.append('offset', String(options.offset));
        if (options.category) params.append('category', options.category);
        if (options.eventType) params.append('eventType', options.eventType);
        if (options.status) params.append('status', options.status);
        if (options.resourceType) params.append('resourceType', options.resourceType);
        if (options.startDate) params.append('startDate', options.startDate);
        if (options.endDate) params.append('endDate', options.endDate);

        const queryString = params.toString();
        const url = queryString ? `/activity?${queryString}` : '/activity';

        const response = await apiClient.get(url);
        return response.data;
    }

    /**
     * Get user's activity statistics
     */
    async getStats(options: { startDate?: string; endDate?: string } = {}): Promise<ActivityStatsResponse> {
        const params = new URLSearchParams();

        if (options.startDate) params.append('startDate', options.startDate);
        if (options.endDate) params.append('endDate', options.endDate);

        const queryString = params.toString();
        const url = queryString ? `/activity/stats?${queryString}` : '/activity/stats';

        const response = await apiClient.get(url);
        return response.data;
    }

    /**
     * Get available activity categories
     */
    async getCategories(): Promise<{ success: boolean; data: { categories: string[] } }> {
        const response = await apiClient.get('/activity/categories');
        return response.data;
    }

    /**
     * Get available event types
     */
    async getEventTypes(): Promise<{ success: boolean; data: { eventTypes: string[] } }> {
        const response = await apiClient.get('/activity/event-types');
        return response.data;
    }

    /**
     * Get a specific activity log entry
     */
    async getActivityById(id: string): Promise<{ success: boolean; data: ActivityLog }> {
        const response = await apiClient.get(`/activity/${id}`);
        return response.data;
    }
}

export const activityService = new ActivityService();
